/* eslint-disable no-underscore-dangle */
import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button';
import AccessGate from '../../../generic/Gates/AccessGate';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { GoProxy } from '../../../../../typings/common';

import Proxy from './Proxy';

/**
 * Go settings page.
 */
const GoSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'go',
  );

  const proxies = settings?.proxies || [];

  const onAdd = () => {
    onUpdateSettings({
      proxies: [...proxies, { _id: nanoid() }],
    });
  };

  const onRemove = (index: number) => {
    onUpdateSettings({
      proxies: dropAt(proxies, index),
    });
  };

  const onUpdateProxy = <K extends keyof GoProxy>(
    index: number,
    member: K,
    value: GoProxy[K],
  ) => {
    onUpdateSettings({
      proxies: proxies.map((proxy, i) => {
        if (i === index) {
          return {
            ...proxy,
            [member]: value,
            hasPassword: member === 'password' ? false : proxy.hasPassword,
          };
        }
        return proxy;
      }),
    });
  };

  return (
    <AccessGate level="premium">
      <Header heading="Go Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Private Proxies">
              <Section.Paragraph>
                If you use a private Go module proxy, add it here so FOSSA can access your modules during analysis.
              </Section.Paragraph>
            </Section.Left>
            <Section.Right>
              {proxies.map((proxy, index) =>
                <Proxy
                  key={proxy._id}
                  url={proxy.url}
                  username={proxy.username}
                  password={proxy.password}
                  hasPassword={proxy.hasPassword}
                  onRemove={() => onRemove(index)}
                  onUpdateProxy={
                    <K extends keyof GoProxy>(
                      member: K,
                      value: GoProxy[K],
                    ) => onUpdateProxy(index, member, value)
                  }
                />,
              )}
              <Button
                type="secondary"
                size="medium"
                onClick={onAdd}
              >
                <IconText icon={icons.plus} text="Add proxy" />
              </Button>
            </Section.Right>
          </Section>
          )}
    </AccessGate>
  );
};

export default GoSettings;
